import {
  arrowHead,
  compareArrowsRowMajor,
  isWellFormedPath,
  type ArrowData,
  type ArrowShape,
} from './arrow';
import { isDirection } from './direction';

/**
 * A logical puzzle board.
 *
 * `activeMask` is row-major, one byte per cell, 1 when the cell is inside the stencil
 * silhouette. Inactive cells may never hold an arrow, but they do **not** block movement:
 * only arrows block arrows.
 */
export interface BoardData {
  readonly width: number;
  readonly height: number;
  readonly activeMask: Uint8Array;
  readonly arrows: readonly ArrowData[];
}

export const MAX_BOARD_DIMENSION = 128;

export function cellIndex(board: Pick<BoardData, 'width'>, x: number, y: number): number {
  return y * board.width + x;
}

export function inBounds(board: BoardData, x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < board.width && y < board.height;
}

export function isActive(board: BoardData, x: number, y: number): boolean {
  return inBounds(board, x, y) && board.activeMask[cellIndex(board, x, y)] === 1;
}

export function createActiveMask(width: number, height: number, fill = 0): Uint8Array {
  const mask = new Uint8Array(width * height);
  if (fill !== 0) mask.fill(1);
  return mask;
}

export function countActiveCells(board: BoardData): number {
  let count = 0;
  for (let i = 0; i < board.activeMask.length; i += 1) {
    if (board.activeMask[i] === 1) count += 1;
  }
  return count;
}

/** Total cells occupied by arrows, which is what board occupancy is measured against. */
export function countOccupiedCells(board: BoardData): number {
  let count = 0;
  for (const arrow of board.arrows) count += arrow.cells.length;
  return count;
}

/**
 * Returns a board whose arrows are ordered by head cell (row-major) with ids reassigned to
 * their index. Every producer of boards funnels through this, so arrow identity is a pure
 * function of geometry rather than of construction order.
 */
export function normalizeBoard(
  width: number,
  height: number,
  activeMask: Uint8Array,
  arrows: readonly ArrowShape[],
): BoardData {
  const sorted = [...arrows].sort(compareArrowsRowMajor);
  return {
    width,
    height,
    activeMask,
    arrows: sorted.map((arrow, index) => ({
      id: index,
      cells: arrow.cells.map((cell) => ({ x: cell.x, y: cell.y })),
      direction: arrow.direction,
    })),
  };
}

export function cloneBoard(board: BoardData): BoardData {
  return {
    width: board.width,
    height: board.height,
    activeMask: Uint8Array.from(board.activeMask),
    arrows: board.arrows.map((arrow) => ({
      id: arrow.id,
      cells: arrow.cells.map((cell) => ({ x: cell.x, y: cell.y })),
      direction: arrow.direction,
    })),
  };
}

/**
 * Maps every occupied grid cell to the arrow covering it, or -1. Used for O(1) hit testing,
 * so tapping anywhere along a long winding arrow selects that arrow.
 */
export function buildArrowCellMap(board: BoardData): Int32Array {
  const map = new Int32Array(board.width * board.height).fill(-1);
  for (const arrow of board.arrows) {
    for (const cell of arrow.cells) {
      map[cellIndex(board, cell.x, cell.y)] = arrow.id;
    }
  }
  return map;
}

export interface BoardValidationIssue {
  readonly code: string;
  readonly detail: string;
}

/** Structural validation. Never trust boards coming from storage, workers or debug imports. */
export function validateBoard(board: BoardData): BoardValidationIssue[] {
  const issues: BoardValidationIssue[] = [];
  const push = (code: string, detail: string) => issues.push({ code, detail });

  if (!Number.isInteger(board.width) || board.width <= 0 || board.width > MAX_BOARD_DIMENSION) {
    push('bad-width', `width=${board.width}`);
  }
  if (!Number.isInteger(board.height) || board.height <= 0 || board.height > MAX_BOARD_DIMENSION) {
    push('bad-height', `height=${board.height}`);
  }
  if (issues.length > 0) return issues;

  if (board.activeMask.length !== board.width * board.height) {
    push('bad-mask-length', `${board.activeMask.length} != ${board.width * board.height}`);
    return issues;
  }
  for (let i = 0; i < board.activeMask.length; i += 1) {
    if (board.activeMask[i] !== 0 && board.activeMask[i] !== 1) {
      push('bad-mask-value', `index ${i} = ${board.activeMask[i]}`);
      break;
    }
  }

  const occupied = new Map<number, number>();
  for (let i = 0; i < board.arrows.length; i += 1) {
    const arrow = board.arrows[i];
    if (arrow.id !== i) push('bad-arrow-id', `arrow at index ${i} has id ${arrow.id}`);
    if (!isDirection(arrow.direction)) push('bad-direction', `arrow ${arrow.id}`);
    if (arrow.cells.length === 0) {
      push('empty-arrow', `arrow ${arrow.id}`);
      continue;
    }
    if (!isWellFormedPath(arrow)) {
      push('malformed-path', `arrow ${arrow.id}`);
      continue;
    }

    for (const cell of arrow.cells) {
      if (!inBounds(board, cell.x, cell.y)) {
        push('arrow-out-of-bounds', `arrow ${arrow.id} at ${cell.x},${cell.y}`);
        continue;
      }
      if (!isActive(board, cell.x, cell.y)) {
        push('arrow-on-inactive-cell', `arrow ${arrow.id} at ${cell.x},${cell.y}`);
      }
      const key = cellIndex(board, cell.x, cell.y);
      const owner = occupied.get(key);
      if (owner !== undefined) {
        push('overlapping-arrows', `arrows ${owner} and ${arrow.id} share ${cell.x},${cell.y}`);
      }
      occupied.set(key, arrow.id);
    }

    if (i > 0 && compareArrowsRowMajor(board.arrows[i - 1], arrow) > 0) {
      push('arrows-unsorted', `arrow ${arrow.id}`);
    }
  }
  return issues;
}

export function assertValidBoard(board: BoardData): void {
  const issues = validateBoard(board);
  if (issues.length > 0) {
    throw new Error(
      `Invalid board: ${issues.map((issue) => `${issue.code} (${issue.detail})`).join(', ')}`,
    );
  }
}

/** Convenience for callers that only care where an arrow will leave from. */
export function headCellIndex(board: BoardData, arrow: ArrowData): number {
  const head = arrowHead(arrow);
  return cellIndex(board, head.x, head.y);
}
